import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from './useAuth'
import { availableModes } from '../lib/availableModes'
import { ensureOfflineReadiness } from '../lib/offlineReadiness'
import { api } from '../lib/api'
import { enforcePackLimit, getPackMeta, pruneCachedModes, recomputePackMeta } from '../lib/offlineDb'

const PILL_STORAGE_KEY = 'maza.selected_university'
const DAILY_REFRESH_MS = 24 * 60 * 60_000
const SYNC_COMPLETE_EVENT = 'tutor:offline-sync-complete'

interface OfflinePackFeatures {
  offline_packs: boolean
  pack_questions_limit: number
}

function readStoredUniversity(): string | null {
  try {
    return sessionStorage.getItem(PILL_STORAGE_KEY)
  } catch {
    return null
  }
}

export function useOfflineReadiness() {
  const location = useLocation()
  const { user } = useAuth()
  const [features, setFeatures] = useState<OfflinePackFeatures | null>(null)

  const isStudentSurface =
    !!user &&
    !user.is_anonymous &&
    !user.is_admin &&
    user.role !== 'admin' &&
    user.role !== 'teacher' &&
    user.role !== 'org_manager'

  // Every surface the student can actually open, from the same predicate
  // StudentTabBar uses. Admission-only students resolve to ['admission'] and
  // behave exactly as before; only students who can reach the 12ª tab get a
  // second pack.
  const modes = availableModes(user?.preferred_mode, user?.is_current_grade_12)
  const modesKey = modes.join(',')

  // The admin cap is a TOTAL across every mode, and it is passed through whole.
  // Splitting it per mode here was wrong twice over: each fill then trimmed the
  // entire store down to one mode's share, so the last fill to land evicted the
  // others, and the integer division silently stranded the remainder. Dividing
  // the cap between modes is enforcePackLimit's job — it water-fills, so a mode
  // that wants less than its share leaves the balance to the others.
  const packLimit = features?.pack_questions_limit ?? 0

  useEffect(() => {
    if (!isStudentSurface) {
      setFeatures(null)
      return
    }
    let cancelled = false
    void api
      .get<{ features: OfflinePackFeatures }>('/students/me/features')
      .then((response) => {
        if (!cancelled) setFeatures(response.features)
      })
      .catch(() => {
        if (!cancelled) setFeatures(null)
      })
    return () => {
      cancelled = true
    }
  }, [isStudentSurface])

  useEffect(() => {
    if (!isStudentSurface) return
    let cancelled = false
    void (async () => {
      const meta = await getPackMeta()
      const removed = await pruneCachedModes(modes)
      if (!cancelled && removed > 0) {
        await recomputePackMeta(meta?.programmeNames ?? [])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isStudentSurface, modesKey])

  useEffect(() => {
    if (!isStudentSurface || !features?.offline_packs || (features.pack_questions_limit ?? 0) <= 0) return
    let cancelled = false
    void (async () => {
      const meta = await getPackMeta()
      const removed = await enforcePackLimit(features.pack_questions_limit, modes)
      if (!cancelled && removed > 0) {
        await recomputePackMeta(meta?.programmeNames ?? [])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [features, isStudentSurface, modesKey])

  function packNeedsRefresh(meta: Awaited<ReturnType<typeof getPackMeta>>): boolean {
    if (!features?.offline_packs || (features.pack_questions_limit ?? 0) <= 0) return false
    if (!meta || meta.totalQuestions <= 0) return true
    if (features.pack_questions_limit > 0 && meta.totalQuestions > features.pack_questions_limit) return true
    const downloadedAt = new Date(meta.downloadedAt).getTime()
    if (!Number.isFinite(downloadedAt)) return true
    return (Date.now() - downloadedAt) >= DAILY_REFRESH_MS
  }

  useEffect(() => {
    if (!isStudentSurface || !features?.offline_packs || (features.pack_questions_limit ?? 0) <= 0) return

    let cancelled = false
    void (async () => {
      const meta = await getPackMeta()
      if (cancelled) return
      const shouldRefresh = packNeedsRefresh(meta)
      if (!shouldRefresh) return

      const params = new URLSearchParams(location.search)
      const subjectFromRoute = params.get('subject')
      const activeSubjectCode = subjectFromRoute ?? user?.active_subject ?? null
      const routeUniversity = params.get('university') ?? readStoredUniversity()

      for (const mode of modes) {
        // University scoping is an admission concept. national_12 papers are
        // published by INEE and the backend pins that mode's institution
        // regardless, so sending a university there is misleading noise.
        const university = mode === 'admission' ? routeUniversity : null

        if (location.pathname === '/questions') {
          void ensureOfflineReadiness({
            priority: 'urgent',
            mode,
            university,
            activeSubjectCode,
            maxQuestions: packLimit,
            packModes: modes,
          })
        }

        void ensureOfflineReadiness({
          priority: 'background',
          mode,
          university,
          activeSubjectCode,
          maxQuestions: packLimit,
          packModes: modes,
        })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [features, isStudentSurface, location.pathname, location.search, user?.active_subject, modesKey, packLimit])

  useEffect(() => {
    if (!isStudentSurface || !features?.offline_packs || (features.pack_questions_limit ?? 0) <= 0) return

    const runBackground = () => {
      void (async () => {
        const meta = await getPackMeta()
        if (!packNeedsRefresh(meta)) return
        for (const mode of modes) {
          void ensureOfflineReadiness({
            priority: 'background',
            mode,
            university: mode === 'admission' ? readStoredUniversity() : null,
            activeSubjectCode: user?.active_subject ?? null,
            maxQuestions: packLimit,
            packModes: modes,
          })
        }
      })()
    }

    const onOnline = () => runBackground()
    const onSyncComplete = () => runBackground()
    window.addEventListener('online', onOnline)
    window.addEventListener(SYNC_COMPLETE_EVENT, onSyncComplete)

    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener(SYNC_COMPLETE_EVENT, onSyncComplete)
    }
  }, [features, isStudentSurface, user?.active_subject, modesKey, packLimit])
}
